import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import Alert from '../../../../shared/components/Alert';
import LoadingSpinner from '../../../../shared/components/widgets/LoadingSpinner';
import style, { DROPDOWN_COLORS } from '../../../../shared/components/themeSupport';
import oauthService from '../../../../services/oauthService';
import { apiFetch, getJwtToken } from '../../../../utils/http';
import { jsonHeader } from '../../../../config';

const REDIRECT_URI = 'https://dashboard.limitless.tech/onboarding/oauth/walmart';

const toOption = seller => ({
  value: seller.seller_account_id,
  label: `${seller.seller_account_name} (${seller.marketplace_name})`,
  // AdJuggernaut is running on Limitless.
  platform: seller.platform === 'adjuggernaut' ? 'limitless' : seller.platform,
});

const LinkWalmartAccountSelect = () => {
  const theme = useSelector(state => state.theme);
  const themeColors = style(theme, DROPDOWN_COLORS);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    apiFetch('/accounts/active-by-name', { signal: controller.signal })
      .then((data) => {
        setOptions(Array.isArray(data) ? data.map(toOption) : []);
        setLoading(false);
      })
      .catch((error) => {
        if (error.name === 'AbortError' || error.name === 'UnauthorizedError') return;
        setAlert({ color: 'danger', message: 'Unable to load your accounts. Please try again.' });
        setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const handleContinue = async () => {
    if (!selected) {
      setAlert({ color: 'warning', message: 'Please select the account you are onboarding.' });
      return;
    }
    setSubmitting(true);
    setAlert(null);
    try {
      const account = await apiFetch('/walmart/onboard/mp-api', { params: { accountId: selected.value } });
      if (!account || !account.walmartSellerId) {
        throw new Error('not-ready');
      }
      oauthService.wmMpOauth({
        options: {
          method: 'POST',
          headers: { ...jsonHeader, authorization: `JWT ${getJwtToken()}` },
          body: JSON.stringify({
            accountId: selected.value,
            redirectUri: REDIRECT_URI,
            sellerId: account.walmartSellerId,
            platform: selected.platform,
          }),
        },
        // On success wmMpOauth redirects the browser to the Walmart authorize page.
        onError: () => {
          setAlert({ color: 'danger', message: 'Unable to start the Walmart authorization. Please try again.' });
          setSubmitting(false);
        },
      });
    } catch (error) {
      if (error.name === 'UnauthorizedError') return;
      const message = error.message === 'not-ready'
        ? 'This account is not ready for Walmart Marketplace API onboarding. Please work with your account manager to confirm a Seller ID is configured.'
        : 'Unable to check this account for Walmart onboarding. Please try again.';
      setAlert({ color: 'danger', message });
      setSubmitting(false);
    }
  };

  return (
    <div className="form">
      {alert && (
        <div className="form__form-group">
          <Alert color={alert.color}>{alert.message}</Alert>
        </div>
      )}
      <div className="form__form-group">
        <span className="form__form-group-label">Select the account you are onboarding</span>
        <Select
          value={selected}
          onChange={choice => setSelected(choice)}
          options={options}
          isDisabled={loading || submitting}
          placeholder="Select an account"
          theme={selectTheme => ({
            ...selectTheme,
            colors: {
              ...selectTheme.colors,
              primary: `${themeColors.colorPrimary}`,
              neutral0: `${themeColors.colorBackground}`,
              neutral80: `${themeColors.colorText}`,
            },
          })}
        />
      </div>
      <LoadingSpinner active={loading || submitting} />
      <div className="form__form-group">
        <button
          type="button"
          className="btn btn-primary account__btn"
          onClick={handleContinue}
          disabled={loading || submitting || !selected}
        >Continue to Walmart
        </button>
      </div>
    </div>
  );
};

export default LinkWalmartAccountSelect;
